import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { IUCNRedListCategory, Kingdom } from '@/api/rest/resources/types/species';

import { SpeciesListByCategory, SpeciesSummary } from './components/ThreatnedSpeciesSummary';

export const ThreatenedSpeciesStatsTile = ({
  threatenedSpeciesSummary,
}: {
  threatenedSpeciesSummary: PlotReportFactElement[];
}) => {
  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='threatened-species-stats-tile'>
      <ThreatenedSpeciesCount threatenedSpecies={threatenedSpeciesSummary} />
    </Stack>
  );
};

const ThreatenedSpeciesCount = ({ threatenedSpecies }: { threatenedSpecies: PlotReportFactElement[] }) => {
  const { t } = useTranslation();

  const categoryToSpeciesList = useCategoryToSpeciesList({ data: threatenedSpecies });

  return (
    <div className='flex flex-col gap-8 lg:gap-4 w-full h-full'>
      <Stack className='m-auto w-full max-h-[400px] overflow-y-auto gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
        <span className='typography-body1Bold'>
          {t('shared.ncaDetail.details.threatenedSpecies.stats.explainer.title')}
        </span>
        <SpeciesSummary categoryToSpeciesList={categoryToSpeciesList} />
      </Stack>
    </div>
  );
};

const useCategoryToSpeciesList = ({ data }: { data: PlotReportFactElement[] }) => {
  const { t } = useTranslation();

  const dataByCategory = data.reduce<SpeciesListByCategory[]>((acc, curr) => {
    const labelValues: {
      category?: IUCNRedListCategory | undefined;
      speciesName?: string | undefined;
      kingdom?: Kingdom | undefined;
      commonName?: string | undefined;
    } = {
      category: undefined,
      speciesName: undefined,
      kingdom: undefined,
      commonName: undefined,
    };
    curr.labels?.forEach((label) => {
      switch (label.name) {
        case PlotReportFactLabelNameEnum.iucn_redlist_category:
          labelValues.category = label.value as IUCNRedListCategory;
          break;
        case PlotReportFactLabelNameEnum.species:
          labelValues.speciesName = label.value as string;
          break;
        case PlotReportFactLabelNameEnum.kingdom:
          labelValues.kingdom = label.value as Kingdom;
          break;
        case PlotReportFactLabelNameEnum.common_name:
          labelValues.commonName = label.value as string;
          break;
        default:
          break;
      }
    });
    const { category, speciesName, kingdom, commonName } = labelValues;

    if (!category || !speciesName) {
      return acc;
    }

    const categoryInAcc = acc.find((c) => c.category === category);

    const speciesEntry = {
      name: speciesName,
      count: curr.value as number,
      kingdom,
      commonName,
    };

    if (categoryInAcc) {
      categoryInAcc.species.push(speciesEntry);
    } else {
      acc.push({
        label: t(`global.iucn.description.${category as IUCNRedListCategory}`),
        category,
        species: [speciesEntry],
      });
    }

    return acc;
  }, []);

  return dataByCategory.map((category) => ({
    ...category,
    species: category.species.sort((a, b) => (b.count ?? 0) - (a.count ?? 0)),
  }));
};
